import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface OfficeData {
  name: string;
  description?: string;
  address?: string;
  managerEmail?: string;
  maxEmployees?: string;
}

interface ValidationResult {
  valid: OfficeData[];
  invalid: Array<{
    row?: number;
    data: OfficeData;
    reason: string;
  }>;
  summary: {
    total: number;
    valid: number;
    invalid: number;
  };
}

interface CreationResult {
  created: Array<{
    _id: string;
    name: string;
    description?: string;
    address?: string;
  }>;
  failed: Array<{
    name: string;
    description?: string;
    address?: string;
    reason: string;
  }>;
  summary: {
    total: number;
    created: number;
    failed: number;
  };
}

const BulkUploadOffices: React.FC = () => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [creationResult, setCreationResult] = useState<CreationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const steps = ['Subir Archivo', 'Revisar Datos', 'Confirmar y Procesar', 'Resultado'];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Por favor selecciona un archivo CSV');
        return;
      }
      setSelectedFile(file);
      setError(null);
      setValidationResult(null);
      setCreationResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Por favor selecciona un archivo');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.validateBulkUploadOffices(formData);
      setValidationResult(response);
      
      if (response.valid.length > 0) {
        setActiveStep(1);
      } else {
        setError('No hay sedes válidas para procesar. Por favor revisa el archivo.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error procesando archivo');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!validationResult || validationResult.valid.length === 0) {
      setError('No hay sedes válidas para procesar');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.createBulkOffices(validationResult.valid);
      setCreationResult(response);
      setActiveStep(3);

      setSnackbar({
        open: true,
        message: `Se crearon ${response.created.length} de ${response.summary.total} sedes exitosamente`,
        severity: response.failed.length > 0 ? 'error' : 'success'
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error creando sedes');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setValidationResult(null);
    setCreationResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'Nombre\nSede Central\nSede Norte';
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'plantilla_sedes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Carga Masiva de Sedes
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stepper */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Paso 1: Subir Archivo */}
        {activeStep === 0 && (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Sube un archivo CSV con las sedes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  El archivo debe contener la columna: Nombre (requerido). Los demás campos son opcionales.
                </Typography>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    size="large"
                    sx={{ mr: 2 }}
                  >
                    Seleccionar Archivo CSV
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    size="large"
                  >
                    Descargar Plantilla
                  </Button>
                </Box>

                {selectedFile && (
                  <Box sx={{ mt: 3 }}>
                    <Chip
                      label={selectedFile.name}
                      onDelete={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      deleteIcon={<DeleteIcon />}
                    />
                  </Box>
                )}

                {selectedFile && (
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleUpload}
                      disabled={loading}
                      startIcon={<UploadIcon />}
                    >
                      {loading ? 'Procesando...' : 'Validar Archivo'}
                    </Button>
                  </Box>
                )}

                {loading && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Paso 2: Revisar Datos */}
        {activeStep === 1 && validationResult && (
          <Card>
            <CardContent>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Revisión de Datos
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                  <Chip
                    label={`Válidas: ${validationResult.summary.valid}`}
                    color="success"
                  />
                  <Chip
                    label={`Inválidas: ${validationResult.summary.invalid}`}
                    color="error"
                  />
                  <Chip
                    label={`Total: ${validationResult.summary.total}`}
                  />
                </Box>
              </Box>

              {validationResult.valid.length > 0 && (
                <Box mb={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    Sedes que se crearán ({validationResult.valid.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Descripción</strong></TableCell>
                          <TableCell><strong>Dirección</strong></TableCell>
                          <TableCell><strong>Email Gerente</strong></TableCell>
                          <TableCell><strong>Max Empleados</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResult.valid.map((office, index) => (
                          <TableRow key={index}>
                            <TableCell>{office.name}</TableCell>
                            <TableCell>{office.description || '-'}</TableCell>
                            <TableCell>{office.address || '-'}</TableCell>
                            <TableCell>{office.managerEmail || '-'}</TableCell>
                            <TableCell>{office.maxEmployees || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {validationResult.invalid.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Sedes que no se crearán ({validationResult.invalid.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Descripción</strong></TableCell>
                          <TableCell><strong>Dirección</strong></TableCell>
                          <TableCell><strong>Razón</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResult.invalid.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.data.name}</TableCell>
                            <TableCell>{item.data.description || '-'}</TableCell>
                            <TableCell>{item.data.address || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.reason}
                                color="error"
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button onClick={handleReset}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  disabled={validationResult.valid.length === 0}
                >
                  Continuar
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Paso 3: Confirmar */}
        {activeStep === 2 && validationResult && (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h5" gutterBottom>
                  Confirmar Creación de Sedes
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Se crearán {validationResult.valid.length} sedes en la empresa.
                </Typography>

                <Box display="flex" justifyContent="center" gap={2}>
                  <Button onClick={() => setActiveStep(1)}>
                    Volver
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleProcess}
                    disabled={loading}
                  >
                    {loading ? 'Procesando...' : 'Procesar Sedes'}
                  </Button>
                </Box>

                {loading && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Paso 4: Resultado */}
        {activeStep === 3 && creationResult && (
          <Card>
            <CardContent>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Resultado de la Carga
                </Typography>
                <Box display="flex" gap={2} mb={2}>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`Creadas: ${creationResult.summary.created}`}
                    color="success"
                  />
                  <Chip
                    icon={<ErrorIcon />}
                    label={`Fallidas: ${creationResult.summary.failed}`}
                    color="error"
                  />
                  <Chip
                    label={`Total: ${creationResult.summary.total}`}
                  />
                </Box>
              </Box>

              {creationResult.created.length > 0 && (
                <Box mb={4}>
                  <Typography variant="subtitle1" gutterBottom>
                    Sedes Creadas Exitosamente ({creationResult.created.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Descripción</strong></TableCell>
                          <TableCell><strong>Dirección</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {creationResult.created.map((office) => (
                          <TableRow key={office._id}>
                            <TableCell>{office.name}</TableCell>
                            <TableCell>{office.description || '-'}</TableCell>
                            <TableCell>{office.address || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {creationResult.failed.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Sedes que No se Pudieron Crear ({creationResult.failed.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Nombre</strong></TableCell>
                          <TableCell><strong>Descripción</strong></TableCell>
                          <TableCell><strong>Dirección</strong></TableCell>
                          <TableCell><strong>Razón</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {creationResult.failed.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.description || '-'}</TableCell>
                            <TableCell>{item.address || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.reason}
                                color="error"
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box display="flex" justifyContent="center" mt={3}>
                <Button
                  variant="contained"
                  onClick={handleReset}
                >
                  Cargar Otro Archivo
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BulkUploadOffices;



